/**
 * Реестр виджетов витрины — единый список `module_id`, по которым
 * супер-администратор включает и выключает содержимое («Видимость
 * витрины», `/admin/modules`, US-09 FR-1).
 *
 * **Правило:** всё, что показывает данные пользователю — левое меню, блоки
 * «Цифрового паспорта», плашки и виджеты «Аналитики», виджеты «Проектов» —
 * гейтится отсюда через `useModulePresent` / `<ModuleGate>`. Выключенный
 * виджет **не рендерится вовсе** и «нет данных» вместо себя не показывает.
 *
 * **Идентификаторы обязаны совпадать с реестром `super_admin_modules` на
 * бэке.** Незарегистрированный на бэке id виден по умолчанию
 * (default-visible, см. `useModulePresent`), поэтому новый виджет сначала
 * появляется на витрине и только после сида — в матрице настроек. Схема
 * имени: `<раздел>.<виджет>`, разделов четыре — `left-menu`,
 * `digital-passport`, `analytics`, `projects` (порядок разделов на экране
 * настроек — `SECTION_ORDER` в `ModulesAdmin`).
 */

export const MODULES = {
  /** Левое меню: блок акима и шесть ключевых показателей территории. */
  leftMenu: {
    akim: 'left-menu.akim',
    population: 'left-menu.population',
    villageCount: 'left-menu.village-count',
    saldoMigration: 'left-menu.saldo-migration',
    avgSalary: 'left-menu.avg-salary',
    unemployed: 'left-menu.unemployed',
    area: 'left-menu.area',
  },

  /** Главная: карточка территории на карте. */
  home: {
    regionCard: 'home.region-card',
  },

  /** «Цифровой паспорт» — блоки уровня области и уровня района. */
  digitalPassport: {
    /** Верхняя плашка СЭР — ряд карточек показателей (US-02 FR-4.4). */
    serIndicators: 'digital-passport.ser-indicators',
    administrativeDivision: 'digital-passport.administrative-division',
    populationNationality: 'digital-passport.population-nationality',
    mediaMonitoring: 'digital-passport.media-monitoring',
    serTable: 'digital-passport.ser-table',
    districtDivision: 'digital-passport.district-division',
    districtPopulation: 'digital-passport.district-population',
    settlements: 'digital-passport.settlement-population',
    cpi: 'digital-passport.cpi',
    districtBudget: 'digital-passport.district-budget',
  },

  /**
   * «Аналитика»: раздел целиком (чип) + каждый его виджет отдельно.
   * Выключенный раздел исчезает из ряда чипов и из витрины «Все»
   * (`analytics-visibility.ts`), выключенный виджет — только сам.
   */
  analytics: {
    population: 'analytics.population',
    populationPlashka: 'analytics.population-plashka',
    populationDynamics: 'analytics.population-dynamics',
    populationSocialGroups: 'analytics.population-social-groups',
    populationDemography: 'analytics.population-demography',
    populationNaturalMovement: 'analytics.population-natural-movement',
    populationLaborMarket: 'analytics.population-labor-market',

    economy: 'analytics.economy',
    economyPlashka: 'analytics.economy-plashka',
    // Блоки первой волны US-35.1; идентификаторы — из каталога бэка
    // (DP-478, `MODULE_REGION_ECONOMY` / `MODULE_PRICES_LABOR`).
    economyRegion: 'analytics.economy-region',
    economyPricesLabor: 'analytics.economy-prices-labor',
    // Виджет «Экономическое развитие» (топ-4 отрасли СЭР) заменён блоком
    // «Экономика региона» (US-35.1 FR-4.2): id `analytics.economy-top4`
    // выведен из употребления и подлежит удалению из сида бэка.
    economyBudgetExecution: 'analytics.economy-budget-execution',
    economyBudgetRevenue: 'analytics.economy-budget-revenue',
    economyMsbSubjects: 'analytics.economy-msb-subjects',
    // Подвиджет «Финансово-кредитная поддержка» снят со страницы
    // (US-35.1 FR-1, DP-477): id `analytics.economy-msb-support` выведен
    // из употребления и подлежит удалению из сида бэка.
    economyLegalTaxesTop: 'analytics.economy-legal-taxes-top',
    economyLegalTaxesOked: 'analytics.economy-legal-taxes-oked',

    social: 'analytics.social',
    // Новый состав подраздела (US-20): плашка «Труд и социальная защита»
    // и два блока. Виджет «Самообеспеченность продуктами питания» снят
    // вместе с прежним составом — id `analytics.social-self-sufficiency`
    // выведен из употребления и подлежит удалению из сида бэка.
    socialPlashka: 'analytics.social-plashka',
    socialEducation: 'analytics.social-education',
    socialHealthcare: 'analytics.social-healthcare',

    agriculture: 'analytics.agriculture',
    // Новый состав подраздела (US-17): сводный модуль и пять блоков
    // детального модуля — каждый отдельно управляемый элемент витрины
    // (US-18 FR-11.2). Прежняя плашка US-04.5 и виджеты «Ежегодное
    // производство мяса» и «Производство молочной продукции» сняты
    // вместе с прежним составом — id `analytics.agriculture-plashka`,
    // `analytics.agriculture-meat`, `analytics.agriculture-dairy`
    // выведены из употребления и подлежат удалению из сида бэка.
    // Идентификаторы — из каталога бэка (DP-427, миграция 0037): экран их
    // не придумывает, незаведённый id считается default-visible и
    // выключить такой виджет нечем (US-09 FR-1.5).
    agricultureSummary: 'analytics.agriculture-summary',
    agricultureProducers: 'analytics.agriculture-producers',
    agricultureProduction: 'analytics.agriculture-livestock-production',
    agricultureHeads: 'analytics.agriculture-headcount',
    agricultureSownArea: 'analytics.agriculture-crop-area',
    agricultureYield: 'analytics.agriculture-crop-yield',
    // Сохранённые виджеты подраздела (US-17 FR-12) — прежние id: их
    // состав и источник (ручной ввод) этим US не меняются. Виджет
    // «Динамика поголовья» снят с подраздела — id
    // `analytics.agriculture-dynamics` выведен из употребления и
    // подлежит удалению из сида бэка.
    agricultureSownAreas: 'analytics.agriculture-sown-areas',
    agricultureLivestock: 'analytics.agriculture-livestock',

    landFund: 'analytics.land-fund',
    landFundPlashka: 'analytics.land-fund-plashka',
    landFundSettlement: 'analytics.land-fund-settlement',
    landFundReserve: 'analytics.land-fund-reserve',
    landFundLandowners: 'analytics.land-fund-landowners',

    infrastructure: 'analytics.infrastructure',
    infrastructurePlashka: 'analytics.infrastructure-plashka',
    infrastructureEngineering: 'analytics.infrastructure-engineering',
    infrastructureWater: 'analytics.infrastructure-water',
    infrastructureRoads: 'analytics.infrastructure-roads',

    construction: 'analytics.construction',
    // Новый состав подраздела (US-23): плашка и два блока. Виджеты
    // прежнего состава US-04.8 (объёмы, сводка и реестр ПСД) сняты
    // вместе с ним — id `construction-volume`, `construction-housing`,
    // `construction-psd`, `construction-psd-registry` выведены из
    // употребления и подлежат удалению из сида бэка.
    constructionPlashka: 'analytics.construction-plashka',
    constructionNonresidential: 'analytics.construction-nonresidential',
    constructionEmergencyHousing: 'analytics.construction-emergency-housing',

    snpNeeds: 'analytics.snp-needs',
    snpNeedsPlashka: 'analytics.snp-needs-plashka',
    snpNeedsVillages: 'analytics.snp-needs-villages',
    snpNeedsEngineering: 'analytics.snp-needs-engineering',
    snpNeedsRoads: 'analytics.snp-needs-roads',
    snpNeedsEducation: 'analytics.snp-needs-education',
    snpNeedsHealthcare: 'analytics.snp-needs-healthcare',

    trade: 'analytics.trade',
    tradePlashka: 'analytics.trade-plashka',
    tradePrices: 'analytics.trade-prices',
    tradeWholesale: 'analytics.trade-wholesale',
    tradeTerritories: 'analytics.trade-territories',
  },

  /**
   * «Проекты»: четыре элемента новой редакции раздела (US-26 FR-1.2) —
   * плашка шести показателей, реестр проектов и два виджета.
   *
   * Идентификаторы — **из каталога бэка** (DP-446, `MODULE_SUMMARY` и
   * соседи в `app/api/public_projects.py`): экран их не придумывает, и
   * незаведённый id считался бы default-visible — выключить такой виджет
   * нечем (US-09 FR-1.5). Три существовали с US-05.1 и переиспользованы
   * под новый состав, новый один — `-largest`.
   *
   * Своего тумблера у карточки проекта нет: она открывается из реестра и
   * из «Крупнейших проектов», и привязка к одному из них оборвала бы
   * второй путь.
   *
   * Сняты вместе с прежним составом US-05.1 (US-26 FR-1.4): id
   * `projects.infrastructure-industries`,
   * `projects.infrastructure-procurement`,
   * `projects.infrastructure-top-risk` выведены из употребления и
   * подлежат удалению из сида бэка.
   */
  projects: {
    investment: 'projects.investment',
    /** Плашка шести показателей — `/public/projects/summary`. */
    plashka: 'projects.infrastructure-portfolio',
    registry: 'projects.infrastructure-registry',
    abp: 'projects.infrastructure-abp',
    largest: 'projects.infrastructure-largest',
  },
} as const;
